import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Search, Printer, Trash2, RefreshCw, ChevronLeft, ChevronRight, X, Users, Plus, FileText } from "lucide-react";

const box = {
  background: "white",
  border: "1px solid #d9d9d9",
  borderRadius: 6,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};

const inputStyle = {
  height: 34,
  border: "1px solid #cfcfcf",
  borderRadius: 4,
  padding: "0 10px",
  fontSize: 13,
  boxSizing: "border-box",
  background: "white",
};

const btn = (bg, color = "white") => ({
  height: 34,
  padding: "0 14px",
  background: bg,
  color,
  border: "none",
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  letterSpacing: 0.2,
});

const nf = (n) => Number(n || 0).toLocaleString("it-IT");

// Pill saldo: bianco su rosso quando negativo, verde quando >= 0.
const SaldoPill = ({ v }) => (
  <span style={{ display: "inline-block", minWidth: 64, textAlign: "center", padding: "3px 10px", borderRadius: 6, fontWeight: 800, fontSize: 13, background: v < 0 ? "#c62828" : "#e8f5e9", color: v < 0 ? "#fff" : "#1b5e20" }}>{nf(v)}</span>
);

// Colonne tabella stampe (ordine e titoli richiesti dall'utente).
const COLS = [
  { key: "data_label", label: "DATA E ORA" },
  { key: "nome_cognome", label: "UTENTE" },
  { key: "stampante", label: "STAMPANTE" },
  { key: "indirizzo", label: "INDIRIZZO IP" },
  { key: "dimensione", label: "Dimensione" },
  { key: "duplex", label: "Duplex" },
  { key: "pagine_colore", label: "COLORE", num: true },
  { key: "pagine_bn", label: "BN", num: true },
  { key: "carta", label: "Carta" },
  { key: "tipo_lavoro", label: "Tipo_di_Lavoro_di_Stampa" },
  { key: "titolo", label: "Nome Doc" },
];

function StampeTable({ items, loading, emptyContent, testid = "stampe-table", rowPrefix = "stampe-row" }) {
  return (
    <div style={{ ...box, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table data-testid={testid} style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#37474f", color: "white", textAlign: "left" }}>
              {COLS.map((c) => (
                <th key={c.key} style={{ padding: "9px 10px", fontWeight: 700, whiteSpace: "nowrap", textAlign: c.num ? "right" : "left" }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={COLS.length} style={{ padding: 30, textAlign: "center", color: "#90a4ae" }}>Caricamento…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={COLS.length} style={{ padding: 40, textAlign: "center", color: "#90a4ae" }}>{emptyContent}</td></tr>
            )}
            {!loading && items.map((it, i) => (
              <tr key={i} data-testid={`${rowPrefix}-${i}`} style={{ borderBottom: "1px solid #eee", background: i % 2 ? "#fafafa" : "white" }}>
                {COLS.map((c) => {
                  const v = it[c.key];
                  if (c.num) return <td key={c.key} style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, color: "#455a64", whiteSpace: "nowrap" }}>{nf(v)}</td>;
                  if (c.key === "titolo") return <td key={c.key} style={{ padding: "7px 10px", color: "#37474f", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v}>{v || "—"}</td>;
                  return <td key={c.key} style={{ padding: "7px 10px", color: "#546e7a", whiteSpace: "nowrap" }}>{v || "—"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StampeListTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [utente, setUtente] = useState("");
  const [stampante, setStampante] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 100 };
      if (utente) params.utente = utente;
      if (stampante) params.stampante = stampante;
      if (q) params.q = q;
      if (dataDa) params.data_da = dataDa;
      if (dataA) params.data_a = dataA;
      const r = await api.get("/stampe", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento log stampe");
    } finally {
      setLoading(false);
    }
  }, [page, utente, stampante, q, dataDa, dataA]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQ(qInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);

  const onFileSelected = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("L'importazione SOSTITUIRÀ tutte le stampe attuali con il contenuto del file. I movimenti manuali degli utenti NON verranno toccati. Continuare?")) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/stampe/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      toast.success(`Import completato: ${d.inseriti || 0} stampe caricate${d.svuotati ? ` (sostituite ${d.svuotati} precedenti)` : ""}${d.riattribuiti ? ` · ${d.riattribuiti} riattribuite al vero utente` : ""}.`);
      setPage(1);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import CSV non riuscito");
    } finally {
      setImporting(false);
    }
  };

  const svuota = async () => {
    if (!window.confirm("Svuotare completamente la tabella STAMPE? L'operazione non è reversibile.")) return;
    try {
      const r = await api.delete("/stampe/all");
      toast.success(`Tabella svuotata (${r.data?.eliminati || 0} record eliminati).`);
      setPage(1);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile svuotare la tabella");
    }
  };

  const resetFiltri = () => {
    setUtente(""); setStampante(""); setQInput(""); setQ(""); setDataDa(""); setDataA(""); setPage(1);
  };

  const items = data?.items || [];
  const facets = data?.facets || { utenti: [], stampanti: [] };
  const perUtente = data?.per_utente || [];
  const perStampante = data?.per_stampante || [];
  const totalCount = data?.total_count || 0;
  const totalePagine = data?.totale_pagine || 0;
  const pageSize = data?.page_size || 100;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const hasFilters = utente || stampante || q || dataDa || dataA;
  const isEmpty = !loading && totalCount === 0 && !hasFilters;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "#78909c" }}>Utilizzo stampanti · import da nddPrint (CSV)</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFileSelected} style={{ display: "none" }} data-testid="stampe-import-input" />
          <button data-testid="stampe-refresh-btn" onClick={load} style={btn("#eceff1", "#455a64")} title="Aggiorna"><RefreshCw size={15} /></button>
          <button data-testid="stampe-import-btn" onClick={() => fileRef.current?.click()} disabled={importing} style={{ ...btn("#2e7d32"), opacity: importing ? 0.7 : 1, cursor: importing ? "wait" : "pointer" }}>
            <Upload size={15} /> {importing ? "Importazione…" : "Importa CSV"}
          </button>
          <button data-testid="stampe-svuota-btn" onClick={svuota} style={btn("#eceff1", "#c62828")} title="Svuota tabella"><Trash2 size={15} /></button>
        </div>
      </div>

      {/* Riepilogo totali */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        {[
          ["Totale pagine" + (hasFilters ? " (filtro)" : ""), totalePagine, "stampe-total-pagine"],
          ["Lavori di stampa", totalCount, "stampe-total-lavori"],
          ["Utenti", facets.utenti.length, null],
          ["Stampanti", facets.stampanti.length, null],
        ].map(([label, val, tid], i) => (
          <div key={i} style={{ ...box, padding: "12px 16px" }}>
            <div style={{ fontSize: 11, color: "#78909c", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
            <div {...(tid ? { "data-testid": tid } : {})} style={{ fontSize: 26, fontWeight: 800, color: "#455a64" }}>{nf(val)}</div>
          </div>
        ))}
      </div>

      {/* Totali per utente / stampante */}
      {(perUtente.length > 0 || perStampante.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12, marginBottom: 16 }}>
          {[["Totale pagine per utente", perUtente, "stampe-per-utente"], ["Totale pagine per stampante/luogo", perStampante, "stampe-per-stampante"]].map(([title, rows, tid]) => (
            <div key={tid} data-testid={tid} style={{ ...box, overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", background: "#455a64", color: "white", fontSize: 12, fontWeight: 700 }}>{title}</div>
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {rows.map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 14px", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
                    <span style={{ color: "#37474f" }}>{r.nome}</span>
                    <span style={{ fontWeight: 700, color: "#455a64" }}>{nf(r.pagine)} <span style={{ color: "#b0bec5", fontWeight: 400 }}>({r.lavori})</span></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filtri */}
      <div style={{ ...box, padding: 12, marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <select data-testid="stampe-filter-utente" value={utente} onChange={(e) => { setPage(1); setUtente(e.target.value); }} style={{ ...inputStyle, minWidth: 180 }}>
          <option value="">Tutti gli utenti</option>
          {facets.utenti.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select data-testid="stampe-filter-stampante" value={stampante} onChange={(e) => { setPage(1); setStampante(e.target.value); }} style={{ ...inputStyle, minWidth: 180 }}>
          <option value="">Tutte le stampanti</option>
          {facets.stampanti.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={{ position: "relative", flex: "1 1 220px", minWidth: 200 }}>
          <Search size={15} color="#90a4ae" style={{ position: "absolute", left: 10, top: 9 }} />
          <input data-testid="stampe-search-titolo" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Cerca titolo…" style={{ ...inputStyle, width: "100%", paddingLeft: 32 }} />
        </div>
        <label style={{ fontSize: 12, color: "#78909c", display: "flex", alignItems: "center", gap: 6 }}>
          Dal <input data-testid="stampe-filter-data-da" type="date" value={dataDa} onChange={(e) => { setPage(1); setDataDa(e.target.value); }} style={inputStyle} />
        </label>
        <label style={{ fontSize: 12, color: "#78909c", display: "flex", alignItems: "center", gap: 6 }}>
          al <input data-testid="stampe-filter-data-a" type="date" value={dataA} onChange={(e) => { setPage(1); setDataA(e.target.value); }} style={inputStyle} />
        </label>
        {hasFilters && (
          <button data-testid="stampe-reset-filtri" onClick={resetFiltri} style={btn("#eceff1", "#546e7a")}><X size={14} /> Azzera filtri</button>
        )}
      </div>

      <StampeTable
        items={items}
        loading={loading}
        emptyContent={isEmpty ? (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#607d8b", marginBottom: 6 }}>Nessun dato importato</div>
            <div style={{ marginBottom: 14 }}>Importa un file CSV esportato da nddPrint per iniziare.</div>
            <button onClick={() => fileRef.current?.click()} style={{ ...btn("#2e7d32"), display: "inline-flex" }}><Upload size={15} /> Importa CSV</button>
          </div>
        ) : "Nessun risultato per i filtri selezionati."}
      />

      {/* Paginazione */}
      {totalCount > pageSize && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 4px", fontSize: 13, color: "#607d8b" }}>
          <span>Pagina {page} di {totalPages} · {nf(totalCount)} righe</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="stampe-prev-page" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ ...btn("#eceff1", "#455a64"), opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? "default" : "pointer" }}><ChevronLeft size={15} /> Prec.</button>
            <button data-testid="stampe-next-page" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ ...btn("#eceff1", "#455a64"), opacity: page >= totalPages ? 0.4 : 1, cursor: page >= totalPages ? "default" : "pointer" }}>Succ. <ChevronRight size={15} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function SaldoCard({ label, saldo, carico, scarico, stampato, tid }) {
  const neg = saldo < 0;
  return (
    <div style={{ ...box, padding: "14px 18px", background: neg ? "#c62828" : "white", borderLeft: `4px solid ${neg ? "#8e0000" : "#2e7d32"}` }}>
      <div style={{ fontSize: 12, color: neg ? "rgba(255,255,255,0.9)" : "#78909c", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div data-testid={tid} style={{ fontSize: 32, fontWeight: 800, color: neg ? "#fff" : "#2e7d32", lineHeight: 1.1 }}>{nf(saldo)}</div>
      <div style={{ fontSize: 12, color: neg ? "rgba(255,255,255,0.9)" : "#78909c", marginTop: 6 }}>
        Caricato <b style={{ color: neg ? "#fff" : "#2e7d32" }}>{nf(carico)}</b> · Scaricato <b style={{ color: neg ? "#fff" : "#ef6c00" }}>{nf(scarico)}</b> · Stampato <b style={{ color: neg ? "#fff" : "#455a64" }}>{nf(stampato)}</b>
      </div>
    </div>
  );
}

function UtentiTab() {
  const [sel, setSel] = useState("");
  const [saldi, setSaldi] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const emptyBulk = { tipo: "carico", data: new Date().toISOString().slice(0, 10), copie_colore: "", copie_bn: "", descrizione: "" };
  const [bulk, setBulk] = useState(emptyBulk);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const emptyForm = { tipo: "carico", data: today, copie_colore: "", copie_bn: "", descrizione: "" };
  const [form, setForm] = useState(emptyForm);

  const loadSaldi = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get("/stampe/saldi");
      setSaldi(r.data?.utenti || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento saldi");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { if (!sel) loadSaldi(); }, [sel, loadSaldi]);

  const applyBulk = async () => {
    const cc = parseInt(bulk.copie_colore || "0", 10) || 0;
    const cb = parseInt(bulk.copie_bn || "0", 10) || 0;
    if (cc <= 0 && cb <= 0) { toast.error("Inserisci almeno una quantità (Colore o B/N)"); return; }
    if (!window.confirm(`Applicare un ${bulk.tipo === "carico" ? "CARICO" : "SCARICO"} di ${cc} Colore e ${cb} B/N a TUTTI gli utenti?`)) return;
    setSavingBulk(true);
    try {
      const r = await api.post("/stampe/movimenti/bulk", { tipo: bulk.tipo, copie_colore: cc, copie_bn: cb, descrizione: bulk.descrizione, data: bulk.data || null });
      toast.success(`Movimento applicato a ${r.data?.utenti || 0} utenti`);
      setShowBulk(false); setBulk(emptyBulk); await loadSaldi();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile applicare il movimento massivo");
    } finally {
      setSavingBulk(false);
    }
  };

  const saldiFiltered = saldi.filter((r) => (r.utente || "").toLowerCase().includes(search.toLowerCase()));

  const loadDetail = useCallback(async (u) => {
    if (!u) { setDetail(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/stampe/dettaglio", { params: { utente: u } });
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento dettaglio utente");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDetail(sel); }, [sel, loadDetail]);

  const saveMov = async () => {
    const cc = parseInt(form.copie_colore || "0", 10) || 0;
    const cb = parseInt(form.copie_bn || "0", 10) || 0;
    if (cc <= 0 && cb <= 0) { toast.error("Inserisci almeno una quantità (Colore o B/N)"); return; }
    setSaving(true);
    try {
      await api.post("/stampe/movimenti", {
        utente: sel, tipo: form.tipo, copie_colore: cc, copie_bn: cb,
        descrizione: form.descrizione, data: form.data || null,
      });
      toast.success("Movimento registrato");
      setShowForm(false);
      setForm(emptyForm);
      await loadDetail(sel);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile salvare il movimento");
    } finally {
      setSaving(false);
    }
  };

  const delMov = async (id) => {
    if (!window.confirm("Eliminare questo movimento?")) return;
    try {
      await api.delete(`/stampe/movimenti/${id}`);
      toast.success("Movimento eliminato");
      await loadDetail(sel);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile eliminare");
    }
  };

  return (
    <div>
      {sel ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <button data-testid="stampe-detail-back" onClick={() => { setSel(""); setShowForm(false); }} style={btn("#eceff1", "#455a64")}><ChevronLeft size={15} /> Elenco utenti</button>
          <span style={{ fontSize: 16, fontWeight: 800, color: "#37474f" }}>{sel}</span>
          <button data-testid="stampe-add-movimento-btn" onClick={() => setShowForm((s) => !s)} style={{ ...btn("#455a64"), marginLeft: "auto" }}><Plus size={15} /> Nuovo movimento</button>
        </div>
      ) : (
        <>
          {/* Toolbar elenco */}
          <div style={{ ...box, padding: 12, marginBottom: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200 }}>
              <Search size={15} color="#90a4ae" style={{ position: "absolute", left: 10, top: 9 }} />
              <input data-testid="stampe-saldi-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca utente…" style={{ ...inputStyle, width: "100%", paddingLeft: 32 }} />
            </div>
            <button data-testid="stampe-saldi-refresh" onClick={loadSaldi} style={btn("#eceff1", "#455a64")} title="Aggiorna"><RefreshCw size={15} /></button>
            <button data-testid="stampe-bulk-btn" onClick={() => setShowBulk((s) => !s)} style={btn("#455a64")}><Users size={15} /> Movimento massivo</button>
          </div>

          {/* Form movimento massivo (stesso movimento per tutti gli utenti) */}
          {showBulk && (
            <div style={{ ...box, padding: 16, marginBottom: 16, borderTop: "3px solid #455a64" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#37474f", marginBottom: 4 }}>Movimento massivo — applica lo stesso movimento a <b>tutti</b> gli utenti</div>
              <div style={{ fontSize: 12, color: "#90a4ae", marginBottom: 12 }}>Utile per assegnare a tutti lo stesso plafond iniziale (carico) o detrarre in blocco (scarico).</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>Tipo
                  <select data-testid="stampe-bulk-tipo" value={bulk.tipo} onChange={(e) => setBulk({ ...bulk, tipo: e.target.value })} style={{ ...inputStyle, minWidth: 140 }}>
                    <option value="carico">Carico (+)</option>
                    <option value="scarico">Scarico (−)</option>
                  </select>
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>Data
                  <input data-testid="stampe-bulk-data" type="date" value={bulk.data} onChange={(e) => setBulk({ ...bulk, data: e.target.value })} style={inputStyle} />
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>Copie Colore
                  <input data-testid="stampe-bulk-colore" type="number" min="0" value={bulk.copie_colore} onChange={(e) => setBulk({ ...bulk, copie_colore: e.target.value })} placeholder="0" style={{ ...inputStyle, width: 120 }} />
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>Copie B/N
                  <input data-testid="stampe-bulk-bn" type="number" min="0" value={bulk.copie_bn} onChange={(e) => setBulk({ ...bulk, copie_bn: e.target.value })} placeholder="0" style={{ ...inputStyle, width: 120 }} />
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px", minWidth: 200 }}>Descrizione
                  <input data-testid="stampe-bulk-descrizione" value={bulk.descrizione} onChange={(e) => setBulk({ ...bulk, descrizione: e.target.value })} placeholder="es. plafond iniziale a.s. 2026/27" style={{ ...inputStyle, width: "100%" }} />
                </label>
                <button data-testid="stampe-bulk-save" onClick={applyBulk} disabled={savingBulk} style={{ ...btn("#2e7d32"), opacity: savingBulk ? 0.7 : 1 }}>{savingBulk ? "Applico…" : "Applica a tutti"}</button>
                <button data-testid="stampe-bulk-cancel" onClick={() => { setShowBulk(false); setBulk(emptyBulk); }} style={btn("#eceff1", "#546e7a")}>Annulla</button>
              </div>
            </div>
          )}

          {/* Tabella saldi utenti */}
          <div style={{ ...box, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table data-testid="stampe-saldi-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#37474f", color: "white", textAlign: "left" }}>
                    <th style={{ padding: "10px 14px", fontWeight: 700 }}>Utente</th>
                    <th style={{ padding: "10px 14px", fontWeight: 700, textAlign: "right" }}>Lavori</th>
                    <th style={{ padding: "10px 14px", fontWeight: 700, textAlign: "right" }}>Saldo Colore</th>
                    <th style={{ padding: "10px 14px", fontWeight: 700, textAlign: "right" }}>Saldo B/N</th>
                    <th style={{ padding: "10px 14px", fontWeight: 700, textAlign: "right" }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingList && <tr><td colSpan={5} style={{ padding: 30, textAlign: "center", color: "#90a4ae" }}>Caricamento…</td></tr>}
                  {!loadingList && saldiFiltered.length === 0 && <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "#90a4ae" }}>Nessun utente. Importa un CSV nella tab Stampe.</td></tr>}
                  {!loadingList && saldiFiltered.map((r, i) => (
                    <tr key={r.utente} data-testid={`stampe-saldi-row-${i}`} style={{ borderBottom: "1px solid #eee", background: i % 2 ? "#fafafa" : "white" }}>
                      <td style={{ padding: "8px 14px", color: "#37474f", fontWeight: 600 }}>{r.utente}</td>
                      <td style={{ padding: "8px 14px", textAlign: "right", color: "#78909c" }}>{nf(r.lavori)}</td>
                      <td style={{ padding: "8px 14px", textAlign: "right" }}><SaldoPill v={r.saldo_colore} /></td>
                      <td style={{ padding: "8px 14px", textAlign: "right" }}><SaldoPill v={r.saldo_bn} /></td>
                      <td style={{ padding: "8px 14px", textAlign: "right" }}>
                        <button data-testid={`stampe-saldi-dettaglio-${i}`} onClick={() => { setSel(r.utente); setShowForm(false); }} style={btn("#eceff1", "#455a64")}>Dettaglio</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {sel && loading && <div style={{ ...box, padding: 40, textAlign: "center", color: "#90a4ae" }}>Caricamento…</div>}

      {sel && !loading && detail && (
        <>
          {/* Saldi */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
            <SaldoCard label="Saldo copie COLORE" tid="stampe-saldo-colore" saldo={detail.saldo_colore} carico={detail.carico_colore} scarico={detail.scarico_colore} stampato={detail.stampato_colore} />
            <SaldoCard label="Saldo copie B/N" tid="stampe-saldo-bn" saldo={detail.saldo_bn} carico={detail.carico_bn} scarico={detail.scarico_bn} stampato={detail.stampato_bn} />
          </div>

          {/* Form movimento */}
          {showForm && (
            <div style={{ ...box, padding: 16, marginBottom: 16, borderTop: "3px solid #455a64" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#37474f", marginBottom: 12 }}>Nuovo movimento per <span style={{ color: "#455a64" }}>{sel}</span></div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>
                  Tipo
                  <select data-testid="stampe-mov-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} style={{ ...inputStyle, minWidth: 140 }}>
                    <option value="carico">Carico (+)</option>
                    <option value="scarico">Scarico (−)</option>
                  </select>
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>
                  Data
                  <input data-testid="stampe-mov-data" type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} style={inputStyle} />
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>
                  Copie Colore
                  <input data-testid="stampe-mov-colore" type="number" min="0" value={form.copie_colore} onChange={(e) => setForm({ ...form, copie_colore: e.target.value })} placeholder="0" style={{ ...inputStyle, width: 120 }} />
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4 }}>
                  Copie B/N
                  <input data-testid="stampe-mov-bn" type="number" min="0" value={form.copie_bn} onChange={(e) => setForm({ ...form, copie_bn: e.target.value })} placeholder="0" style={{ ...inputStyle, width: 120 }} />
                </label>
                <label style={{ fontSize: 12, color: "#78909c", display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px", minWidth: 200 }}>
                  Descrizione
                  <input data-testid="stampe-mov-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="es. ricarica risma / rettifica" style={{ ...inputStyle, width: "100%" }} />
                </label>
                <button data-testid="stampe-mov-save" onClick={saveMov} disabled={saving} style={{ ...btn("#2e7d32"), opacity: saving ? 0.7 : 1 }}>{saving ? "Salvataggio…" : "Salva"}</button>
                <button data-testid="stampe-mov-cancel" onClick={() => { setShowForm(false); setForm(emptyForm); }} style={btn("#eceff1", "#546e7a")}>Annulla</button>
              </div>
            </div>
          )}

          {/* Movimenti */}
          <div data-testid="stampe-movimenti-table" style={{ ...box, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "8px 14px", background: "#455a64", color: "white", fontSize: 12, fontWeight: 700 }}>Movimenti manuali ({detail.movimenti.length})</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#78909c", fontSize: 11, textTransform: "uppercase" }}>
                    <th style={{ padding: "8px 14px" }}>Data</th>
                    <th style={{ padding: "8px 14px" }}>Tipo</th>
                    <th style={{ padding: "8px 14px", textAlign: "right" }}>Colore</th>
                    <th style={{ padding: "8px 14px", textAlign: "right" }}>B/N</th>
                    <th style={{ padding: "8px 14px" }}>Descrizione</th>
                    <th style={{ padding: "8px 14px", textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {detail.movimenti.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#b0bec5" }}>Nessun movimento manuale. Usa "Nuovo movimento" per aggiungere un carico o uno scarico.</td></tr>
                  )}
                  {detail.movimenti.map((m, i) => {
                    const carico = m.tipo === "carico";
                    const seg = carico ? "+" : "−";
                    return (
                      <tr key={m.id} data-testid={`stampe-mov-row-${i}`} style={{ borderTop: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "8px 14px", color: "#607d8b", whiteSpace: "nowrap" }}>{m.data_label || "—"}</td>
                        <td style={{ padding: "8px 14px" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: carico ? "#e8f5e9" : "#fff3e0", color: carico ? "#2e7d32" : "#ef6c00" }}>{carico ? "Carico" : "Scarico"}</span>
                        </td>
                        <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, color: m.copie_colore ? (carico ? "#2e7d32" : "#ef6c00") : "#cfd8dc" }}>{m.copie_colore ? `${seg}${nf(m.copie_colore)}` : "—"}</td>
                        <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, color: m.copie_bn ? (carico ? "#2e7d32" : "#ef6c00") : "#cfd8dc" }}>{m.copie_bn ? `${seg}${nf(m.copie_bn)}` : "—"}</td>
                        <td style={{ padding: "8px 14px", color: "#546e7a" }}>{m.descrizione || "—"}</td>
                        <td style={{ padding: "8px 14px", textAlign: "right" }}>
                          <button data-testid={`stampe-mov-del-${i}`} onClick={() => delMov(m.id)} title="Elimina" style={{ background: "none", border: "none", cursor: "pointer", color: "#c62828", padding: 4 }}><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dettaglio stampe */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 2px 10px" }}>
            <FileText size={16} color="#455a64" />
            <span style={{ fontSize: 14, fontWeight: 800, color: "#37474f" }}>Dettaglio stampe</span>
            <span style={{ fontSize: 12, color: "#78909c" }}>· {nf(detail.totale_stampe)} lavori · Colore {nf(detail.stampato_colore)} pag · B/N {nf(detail.stampato_bn)} pag</span>
          </div>
          <StampeTable
            items={detail.stampe}
            loading={false}
            testid="stampe-utente-detail-table"
            rowPrefix="stampe-utente-row"
            emptyContent="Nessuna stampa registrata per questo utente."
          />
        </>
      )}
    </div>
  );
}

export default function StampePage() {
  const [tab, setTab] = useState("stampe");
  const tabStyle = (active) => ({
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    border: "none",
    background: "none",
    color: active ? "#455a64" : "#90a4ae",
    borderBottom: active ? "3px solid #455a64" : "3px solid transparent",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  });

  return (
    <div data-testid="stampe-page" style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <Printer size={22} color="#455a64" />
        <div style={{ fontSize: 18, fontWeight: 800, color: "#263238" }}>Stampe</div>
      </div>
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e0e0e0", marginBottom: 20 }}>
        <button data-testid="stampe-tab-stampe" onClick={() => setTab("stampe")} style={tabStyle(tab === "stampe")}><Printer size={16} /> Stampe</button>
        <button data-testid="stampe-tab-utenti" onClick={() => setTab("utenti")} style={tabStyle(tab === "utenti")}><Users size={16} /> Utenti</button>
      </div>
      {tab === "stampe" ? <StampeListTab /> : <UtentiTab />}
    </div>
  );
}
